import type { ComponentPropsWithoutRef } from 'react';

import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';

export type ClampLines = 1 | 2 | 3 | 4 | 5 | 6;

// Tailwind resolves `line-clamp-*` at build time from source text, so the line
// count cannot be interpolated — each reachable value is spelled out here.
const clampedTextVariants = cva('', {
  variants: {
    lines: {
      1: 'line-clamp-1',
      2: 'line-clamp-2',
      3: 'line-clamp-3',
      4: 'line-clamp-4',
      5: 'line-clamp-5',
      6: 'line-clamp-6',
    },
  },
});

// `lines` is required — a clamp without a line count has nothing to clamp to —
// so it is intersected back as a required key over VariantProps' optional one.
export type ClampedTextProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof clampedTextVariants> & { lines: ClampLines };

/**
 * Truncates its content to a fixed number of lines with an ellipsis.
 *
 * Renders only the clamp: reserving vertical space so siblings in a grid align
 * is the consumer's geometry, passed via `className`.
 */
export function ClampedText({ className, lines, ...props }: ClampedTextProps) {
  return (
    <div
      data-slot="clamped-text"
      className={clsxm(clampedTextVariants({ lines }), className)}
      {...props}
    />
  );
}

export { clampedTextVariants };
